import React, { useEffect } from "react";

const ArticleMarcus = ({ onBack }) => {
  const [progress, setProgress] = React.useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => {
      const doc = document.documentElement;
      const pct = (doc.scrollTop / (doc.scrollHeight - doc.clientHeight)) * 100;
      setProgress(Math.min(pct, 100));
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 60,
          left: 0,
          height: 2,
          background: "var(--accent)",
          zIndex: 999,
          width: progress + "%",
          transition: "width 0.1s linear",
        }}
      />
      <article className="article-wrap" style={s.wrap}>
        <div style={s.meta}>
          <span style={s.tag}>Stoicism</span>
          <span style={s.metaInfo}>March 2025 · 4 min read</span>
        </div>

        <h1 style={s.title}>What Marcus Aurelius taught me about debugging</h1>

        <p style={s.subtitle}>
          "You have power over your mind, not outside events. Realise this, and
          you will find strength." I first read that line at 2am, mid-production
          incident, and something clicked.
        </p>

        <div style={s.byline}>
          <div style={s.avatar}>RK</div>
          <div>
            <div style={s.bylineName}>Ramakrishna Katragadda</div>
            <div style={s.bylineRole}>
              Software Engineer · Bottomline Technologies
            </div>
          </div>
        </div>

        <div style={s.body}>
          <p style={s.p}>
            It was a Friday evening. The kind you don't want to be at work for.
            A payment processing workflow had been broken for three hours,
            affecting real customers, and nobody could figure out why. I was
            deep in it — logs open in six tabs, Slack pinging every two minutes,
            a senior engineer hovering — and I was spiralling.
          </p>
          <p style={s.p}>
            Not because the bug was unfixable. Bugs are always fixable. I was
            spiralling because I was spending more energy{" "}
            <strong style={s.strong}>reacting to the situation</strong> than
            actually thinking about the problem.
          </p>

          <blockquote style={s.pullQuote}>
            <p style={s.pullText}>
              "You have power over your mind, not outside events. Realise this,
              and you will find strength."
            </p>
            <cite style={s.pullCite}>— Marcus Aurelius, Meditations</cite>
          </blockquote>

          <p style={s.p}>
            I'd been reading Meditations on and off for a few months — the kind
            of book you pick up when you're feeling philosophical and put down
            when life gets busy. That night, a line I'd half-remembered
            surfaced:{" "}
            <em>you have power over your mind, not outside events.</em> And I
            thought: that's literally what debugging is.
          </p>

          <h2 style={s.h2}>The dichotomy of control, in code</h2>

          <p style={s.p}>
            Epictetus — Marcus Aurelius's philosophical predecessor — taught
            that everything in life falls into one of two buckets: things within
            our control, and things outside it. Within our control: our
            judgments, our intentions, our reactions. Outside it: everything
            else. Other people's actions. Production going down. The pile of
            tech debt someone left three years ago.
          </p>
          <p style={s.p}>
            When a bug surfaces, the natural instinct is to treat the entire
            situation as one giant problem. The broken feature, the Slack
            messages, the pressure from the team, the fact that it happened on a
            Friday — all of it fused into one overwhelming thing. That's exactly
            the wrong framing.
          </p>

          <div className="article-code-block" style={s.codeBlock}>
            <pre style={s.pre}>{`// What we do (unconsciously)
const problem = {
  brokenCode: true,        // within control
  slackPressure: true,     // not within control
  fridayEvening: true,     // not within control
  customerImpact: true,    // not within control
};

// What Stoicism asks us to do
const myActualProblem = {
  brokenCode: true,        // this is all I can fix
};`}</pre>
          </div>

          <p style={s.p}>
            The moment I separated what I could control from what I couldn't,
            the noise dropped away. I couldn't make the incident shorter by
            worrying. What I <strong style={s.strong}>could</strong> do was read
            the logs carefully, form a hypothesis, test it, and iterate.
          </p>

          <h2 style={s.h2}>Equanimity isn't indifference</h2>

          <p style={s.p}>
            There's a version of "stay calm" that means "don't care." That's not
            what the Stoics meant. Marcus Aurelius cared deeply — he ran an
            empire, fought wars, watched people he loved die. Equanimity isn't
            the absence of feeling. It's the refusal to let feeling become noise
            that obscures clear thinking.
          </p>
          <p style={s.p}>
            In practice: I can care that the bug affected customers. I can feel
            the urgency. I just can't let those feelings become the reason I
            start guessing instead of reasoning. Guessing is expensive. A calm,
            methodical hypothesis is cheap.
          </p>

          <div style={s.callout}>
            <div style={s.calloutLabel}>// the pattern that emerged</div>
            <p style={s.calloutText}>
              Observe without judgment first. Name what you know. Name what you
              don't. Form one hypothesis. Test it. The answer is always
              somewhere in the logs — you just have to be quiet enough to read
              them properly.
            </p>
          </div>

          <h2 style={s.h2}>The obstacle is the way</h2>

          <p style={s.p}>
            Ryan Holiday popularised a Stoic idea I think about constantly in
            engineering:
            <em> the obstacle is the way.</em> The impediment to action advances
            action. What stands in the way becomes the way.
          </p>
          <p style={s.p}>
            Every bug I've ever fixed has taught me something. Not the bugs that
            went smoothly — the ones that took three hours on a Friday, where I
            had to question every assumption I'd made about the system. Those
            are the ones that made me better at reading code, understanding data
            flows, thinking about edge cases.
          </p>
          <p style={s.p}>
            The production incident that night? It turned out to be a timezone
            handling edge case in a date comparison — the kind of thing that
            only surfaces in certain geographies under specific conditions. I
            would never have found it if I hadn't been forced to read every line
            of that workflow slowly and carefully.
          </p>

          <div style={s.sectionBreak}>§</div>

          <h2 style={s.h2}>A debugging philosophy, not a debugging trick</h2>

          <p style={s.p}>
            I'm not saying read Meditations and you'll write bug-free code. What
            I am saying is that the mental posture you bring to hard problems
            matters enormously — and Stoicism gives you a surprisingly practical
            framework for that.
          </p>
          <p style={s.p}>
            When something breaks in production, the question isn't{" "}
            <em>why is this happening to me.</em>
            It's{" "}
            <em>
              what do I actually know, what am I assuming, and what's the next
              smallest thing I can test?
            </em>
          </p>
          <p style={s.p}>
            <strong style={s.strong}>
              Separate the signal from the noise.
            </strong>{" "}
            Focus only on what's in your control. Treat the hard bug as the
            thing that will teach you the most. Move through it without drama,
            without panic — and you'll find you get to the answer faster and
            leave the codebase a little better than you found it.
          </p>
          <p
            style={{
              ...s.p,
              fontFamily: "var(--serif)",
              fontStyle: "italic",
              color: "var(--muted)",
              fontSize: "0.95rem",
            }}
          >
            Marcus Aurelius probably never dealt with a JavaScript timezone bug.
            But I think he would have debugged it just fine.
          </p>
        </div>

        <div style={s.footer}>
          <button onClick={onBack} style={s.backBtn}>
            ← Back to portfolio
          </button>
        </div>
      </article>
    </>
  );
};

const s = {
  wrap: { maxWidth: 680, margin: "0 auto" },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "1.75rem",
    flexWrap: "wrap",
  },
  tag: {
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "var(--accent)",
    border: "1px solid rgba(192,132,252,0.25)",
    padding: "3px 10px",
    borderRadius: 20,
  },
  metaInfo: {
    fontSize: "0.68rem",
    color: "var(--muted)",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  title: {
    fontFamily: "var(--serif)",
    fontSize: "clamp(1.8rem,4vw,2.8rem)",
    color: "var(--text)",
    lineHeight: 1.15,
    marginBottom: "1.25rem",
  },
  subtitle: {
    fontFamily: "var(--serif)",
    fontStyle: "italic",
    fontSize: "1.1rem",
    color: "var(--muted2)",
    lineHeight: 1.7,
    marginBottom: "2.5rem",
    paddingBottom: "2rem",
    borderBottom: "1px solid var(--border)",
  },
  byline: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "3rem",
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "var(--accent3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "var(--serif)",
    fontSize: "1rem",
    color: "#fff",
    flexShrink: 0,
  },
  bylineName: { fontSize: "0.82rem", color: "var(--text)", fontWeight: 500 },
  bylineRole: {
    fontSize: "0.7rem",
    color: "var(--muted)",
    letterSpacing: "0.04em",
  },
  body: { fontSize: "1rem", lineHeight: 1.95, color: "var(--muted2)" },
  p: {
    marginBottom: "1.6rem",
    fontSize: "0.95rem",
    lineHeight: 1.95,
    color: "var(--muted2)",
  },
  strong: { color: "var(--text)", fontWeight: 500 },
  h2: {
    fontFamily: "var(--serif)",
    fontSize: "1.4rem",
    color: "var(--text)",
    margin: "2.75rem 0 1.1rem",
    fontWeight: 400,
    lineHeight: 1.3,
  },
  pullQuote: {
    borderLeft: "2px solid var(--accent)",
    padding: "1.1rem 1.5rem",
    margin: "2.25rem 0",
    background: "rgba(192,132,252,0.04)",
    borderRadius: "0 6px 6px 0",
  },
  pullText: {
    fontFamily: "var(--serif)",
    fontStyle: "italic",
    fontSize: "1.1rem",
    color: "var(--text)",
    lineHeight: 1.7,
    marginBottom: 0,
  },
  pullCite: {
    display: "block",
    fontFamily: "var(--mono)",
    fontStyle: "normal",
    fontSize: "0.62rem",
    color: "var(--muted)",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    marginTop: "0.65rem",
  },
  codeBlock: {
    background: "var(--bg2)",
    border: "1px solid var(--border2)",
    borderRadius: 6,
    padding: "1.25rem 1.5rem",
    margin: "1.75rem 0",
    overflowX: "auto",
  },
  pre: {
    fontFamily: "var(--mono)",
    fontSize: "0.8rem",
    color: "var(--muted2)",
    lineHeight: 1.8,
    whiteSpace: "pre",
  },
  callout: {
    background: "var(--bg2)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    padding: "1.25rem 1.5rem",
    margin: "1.75rem 0",
  },
  calloutLabel: {
    fontSize: "0.62rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "var(--accent)",
    marginBottom: "0.5rem",
  },
  calloutText: {
    fontSize: "0.84rem",
    color: "var(--muted2)",
    lineHeight: 1.8,
    margin: 0,
  },
  sectionBreak: {
    textAlign: "center",
    color: "var(--muted)",
    fontSize: "1.2rem",
    margin: "2.5rem 0",
    letterSpacing: "0.3em",
  },
  footer: {
    marginTop: "4rem",
    paddingTop: "2rem",
    borderTop: "1px solid var(--border)",
  },
  backBtn: {
    fontFamily: "var(--mono)",
    fontSize: "0.72rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "var(--muted2)",
    background: "transparent",
    border: "1px solid var(--border2)",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
    transition: "color 0.2s, border-color 0.2s",
  },
};

export default ArticleMarcus;
